import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
const CHAIR_IMG = 'https://cdn.magicpatterns.com/uploads/tJeWe6Uo8fYm4gRHVQhCcL/u8896349413_Cinematic_photo_of_a_vintage_barber_chair_in_a_mo_c4ba5aaf-e4f4-4e9e-a189-eaa260c04ed8_0.png';
// ── Clipper SVG ──────────────────────────────────────────────
function ClipperSVG({
  glowing
}: {
  glowing: boolean;
}) {
  return <svg width="120" height="60" viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
    filter: glowing ? 'drop-shadow(0 0 12px #fff) drop-shadow(0 0 24px rgba(196,30,58,0.9)) drop-shadow(0 0 4px #fff)' : 'drop-shadow(0 2px 8px rgba(0,0,0,0.8))'
  }} data-id="element-111">
      {/* Body */}
      <rect x="10" y="14" width="80" height="32" rx="8" fill="#1a1a1a" data-id="element-112" />
      <rect x="10" y="14" width="80" height="32" rx="8" stroke="#3a3a3a" strokeWidth="1.5" data-id="element-113" />
      {/* Grip texture */}
      {[20, 28, 36, 44, 52, 60, 68].map(x => <rect key={x} x={x} y="20" width="2" height="20" rx="1" fill="#2a2a2a" data-id="element-114" />)}
      {/* Top accent */}
      <rect x="14" y="16" width="72" height="4" rx="2" fill="#2d2d2d" data-id="element-115" />
      {/* Power button */}
      <circle cx="78" cy="30" r="5" fill="#111" stroke="#444" strokeWidth="1" data-id="element-116" />
      <circle cx="78" cy="30" r="2.5" fill={glowing ? '#c41e3a' : '#333'} data-id="element-117" />
      {/* Brand line */}
      <rect x="20" y="27" width="40" height="2" rx="1" fill="#333" data-id="element-118" />
      {/* Blade housing */}
      <rect x="88" y="18" width="22" height="24" rx="3" fill="#222" stroke="#444" strokeWidth="1" data-id="element-119" />
      {/* Blade teeth */}
      {[0, 3, 6, 9, 12, 15, 18].map(i => <rect key={i} x={107 + (i === 0 ? 0 : 0)} y={20 + i} width={i % 2 === 0 ? 8 : 6} height="2" rx="0.5" fill={glowing ? '#ffffff' : '#888'} data-id="element-120" />)}
      {/* Blade glow line */}
      {glowing && <rect x="108" y="19" width="10" height="22" rx="1" fill="url(#bladeGlow)" opacity="0.8" data-id="element-121" />}
      {/* Cord */}
      <path d="M10 30 Q-5 30 -10 40" stroke="#333" strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-122" />
      <defs data-id="element-123">
        <linearGradient id="bladeGlow" x1="0" y1="0" x2="1" y2="0" data-id="element-124">
          <stop offset="0%" stopColor="rgba(255,255,255,0)" data-id="element-125" />
          <stop offset="50%" stopColor="rgba(255,255,255,0.9)" data-id="element-126" />
          <stop offset="100%" stopColor="rgba(196,30,58,0.6)" data-id="element-127" />
        </linearGradient>
      </defs>
    </svg>;
}
// ── Spark particles ──────────────────────────────────────────
function Sparks({
  x,
  y,
  active
}: {
  x: number;
  y: number;
  active: boolean;
}) {
  if (!active) return null;
  const sparks = Array.from({
    length: 12
  }, (_, i) => ({
    angle: i / 12 * 360,
    dist: Math.random() * 40 + 15,
    delay: Math.random() * 0.1,
    size: Math.random() * 3 + 1
  }));
  return <div className="absolute pointer-events-none" style={{
    left: x,
    top: y,
    zIndex: 100
  }} data-id="element-128">
      {sparks.map((s, i) => <motion.div key={i} className="absolute rounded-full" style={{
      width: s.size,
      height: s.size,
      background: i % 3 === 0 ? '#fff' : i % 3 === 1 ? '#c41e3a' : '#ffd700',
      left: 0,
      top: 0
    }} initial={{
      x: 0,
      y: 0,
      opacity: 1,
      scale: 1
    }} animate={{
      x: Math.cos(s.angle * Math.PI / 180) * s.dist,
      y: Math.sin(s.angle * Math.PI / 180) * s.dist,
      opacity: 0,
      scale: 0
    }} transition={{
      duration: 0.4 + s.delay,
      ease: 'easeOut'
    }} data-id="element-129" />)}
    </div>;
}
// ── Barber pole strip ────────────────────────────────────────
function BarberPoleStrip({
  reverse = false
}: {
  reverse?: boolean;
}) {
  return <div className="absolute top-0 bottom-0 w-3 md:w-4 overflow-hidden z-20 pointer-events-none" style={{
    [reverse ? 'right' : 'left']: 0
  }} aria-hidden="true" data-id="element-130">
      <div style={{
      position: 'absolute',
      inset: 0,
      background: `repeating-linear-gradient(45deg,#ffffff 0px,#ffffff 10px,#003087 10px,#003087 20px,#ffffff 20px,#ffffff 30px,#c8102e 30px,#c8102e 40px)`,
      backgroundSize: '56.57px 56.57px',
      animation: `splashPole 1.6s linear infinite${reverse ? ' reverse' : ''}`
    }} data-id="element-131" />
      <div style={{
      position: 'absolute',
      inset: 0,
      background: reverse ? 'linear-gradient(to left, rgba(0,0,0,0.35), transparent)' : 'linear-gradient(to right, rgba(0,0,0,0.35), transparent)'
    }} data-id="element-132" />
    </div>;
}
// ── Main component ───────────────────────────────────────────
export function LanguageSplash() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const [hovering, setHovering] = useState<'en' | 'es' | null>(null);
  const [exiting, setExiting] = useState(false);
  const [phase, setPhase] = useState<'intro' | 'reveal' | 'ui'>('intro');
  const [clipperX, setClipperX] = useState(-15);
  const [cutProgress, setCutProgress] = useState(0); // 0–100%
  const [sparksPos, setSparksPos] = useState({
    x: 0,
    y: 0
  });
  const [showSparks, setShowSparks] = useState(false);
  const rafRef = useRef<number>();
  const startRef = useRef<number>();
  useEffect(() => {
    if (language !== null) return;
    // Delay intro start slightly
    const timeout = setTimeout(() => {
      const duration = 1800; // ms for clipper to cross screen
      const animate = (ts: number) => {
        if (!startRef.current) startRef.current = ts;
        const elapsed = ts - startRef.current;
        const progress = Math.min(elapsed / duration, 1);
        // easeInOut
        const eased = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        const xPct = -15 + eased * 130; // -15% to 115%
        setClipperX(xPct);
        setCutProgress(eased * 100);
        // Sparks at blade tip
        const screenW = window.innerWidth;
        setSparksPos({
          x: xPct / 100 * screenW + 110,
          y: window.innerHeight / 2
        });
        setShowSparks(progress > 0.05 && progress < 0.95);
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animate);
        } else {
          setShowSparks(false);
          setPhase('reveal');
          setTimeout(() => setPhase('ui'), 600);
        }
      };
      rafRef.current = requestAnimationFrame(animate);
    }, 300);
    return () => {
      clearTimeout(timeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [language]);
  if (language !== null) return null;
  const handleSelect = (lang: 'en' | 'es') => {
    if (exiting) return;
    setExiting(true);
    setTimeout(() => setLanguage(lang), 500);
  };
  return <>
      <style data-id="element-133">{`
        @keyframes splashPole {
          0% { background-position: 0 0; }
          100% { background-position: 56.57px 56.57px; }
        }
        @keyframes bladeVibrate {
          0%, 100% { transform: translateY(0px); }
          25% { transform: translateY(-1px); }
          75% { transform: translateY(1px); }
        }
        @keyframes glowPulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
      `}</style>

      <AnimatePresence data-id="element-134">
        {!exiting && <motion.div key="splash" initial={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeInOut'
      }} className="fixed inset-0 z-[9999] overflow-hidden" style={{
        background: '#0d0d0d'
      }} data-id="element-135">
            {/* Background image — always present */}
            <img src={CHAIR_IMG} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-center" style={{
          filter: 'brightness(0.18) saturate(0.5)',
          transition: 'filter 0.5s ease'
        }} data-id="element-136" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" data-id="element-137" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/60" data-id="element-138" />

            {/* ── INTRO PHASE: Black overlay that gets "cut" away ── */}
            {phase === 'intro' && <>
                {/* Left black panel — shrinks as clipper moves right */}
                <div className="absolute top-0 bottom-0 left-0 bg-[#0d0d0d] z-30" style={{
            width: `${cutProgress}%`,
            transition: 'none'
          }} data-id="element-139" />
                {/* Right black panel — always covers uncut area */}
                <div className="absolute top-0 bottom-0 right-0 bg-[#0d0d0d] z-30" style={{
            width: `${100 - cutProgress}%`,
            transition: 'none'
          }} data-id="element-140" />

                {/* Cut line glow — trails behind clipper */}
                <div className="absolute top-0 bottom-0 z-40 pointer-events-none" style={{
            left: `calc(${cutProgress}% - 2px)`,
            width: '4px',
            background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.9) 20%, rgba(196,30,58,1) 50%, rgba(255,255,255,0.9) 80%, transparent 100%)',
            boxShadow: '0 0 20px 6px rgba(196,30,58,0.8), 0 0 40px 12px rgba(255,255,255,0.3)',
            animation: 'glowPulse 0.1s linear infinite'
          }} data-id="element-141" />

                {/* Clipper machine */}
                <div className="absolute z-50 pointer-events-none" style={{
            left: `${clipperX}%`,
            top: '50%',
            transform: 'translateY(-50%)',
            animation: 'bladeVibrate 0.05s linear infinite',
            filter: 'drop-shadow(-8px 0 16px rgba(196,30,58,0.4))'
          }} data-id="element-142">
                  <ClipperSVG glowing={true} data-id="element-143" />
                  {/* Motion blur trail */}
                  {[1, 2, 3].map(i => <div key={i} className="absolute inset-0 pointer-events-none" style={{
              transform: `translateX(-${i * 12}px)`,
              opacity: 0.15 / i
            }} data-id="element-144">
                      <ClipperSVG glowing={false} data-id="element-145" />
                    </div>)}
                </div>

                {/* Sparks */}
                <Sparks x={sparksPos.x} y={sparksPos.y} active={showSparks} data-id="element-146" />

                {/* Screen flash at start */}
                <motion.div className="absolute inset-0 z-50 pointer-events-none bg-white" initial={{
            opacity: 0.6
          }} animate={{
            opacity: 0
          }} transition={{
            duration: 0.3,
            delay: 0.3
          }} data-id="element-147" />
              </>}

            {/* ── REVEAL PHASE: Cut complete flash ── */}
            {phase === 'reveal' && <motion.div className="absolute inset-0 z-40 pointer-events-none" style={{
          background: 'rgba(255,255,255,0.15)'
        }} initial={{
          opacity: 1
        }} animate={{
          opacity: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-148" />}

            {/* ── UI PHASE: Normal splash content ── */}
            <AnimatePresence data-id="element-149">
              {phase === 'ui' && <motion.div className="absolute inset-0 flex flex-col items-center justify-center" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5
          }} data-id="element-150">
                  <BarberPoleStrip data-id="element-151" />
                  <BarberPoleStrip reverse data-id="element-152" />

                  {/* Red glow */}
                  <motion.div className="absolute inset-0 pointer-events-none" animate={{
              background: hovering === 'en' ? 'radial-gradient(ellipse 60% 60% at 25% 50%, rgba(196,30,58,0.18) 0%, transparent 70%)' : hovering === 'es' ? 'radial-gradient(ellipse 60% 60% at 75% 50%, rgba(196,30,58,0.18) 0%, transparent 70%)' : 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(196,30,58,0.08) 0%, transparent 70%)'
            }} transition={{
              duration: 0.5
            }} data-id="element-153" />

                  {/* Center divider */}
                  <motion.div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px pointer-events-none" style={{
              background: 'rgba(196,30,58,0.3)'
            }} initial={{
              scaleY: 0
            }} animate={{
              scaleY: 1
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} data-id="element-154" />

                  {/* Logo */}
                  <motion.div className="absolute top-8 left-1/2 -translate-x-1/2 flex flex-col items-center z-10" initial={{
              opacity: 0,
              y: -16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} data-id="element-155">
                    <img src="https://cdn.magicpatterns.com/uploads/xrrzKKYawcAGiPC3xHK7VT/Jonathans_Barber_Shop_White_Transparent.png" alt="Jonathan's Barbershop" className="h-14 md:h-20 w-auto object-contain opacity-90 mix-blend-screen" data-id="element-156" />
                    <div className="mt-3 w-12 h-px bg-[#c41e3a]" data-id="element-157" />
                  </motion.div>

                  {/* Language prompt */}
                  <motion.div className="relative z-10 flex flex-col items-center mb-10 px-4 text-center" initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.15
            }} data-id="element-158">
                    <p className="text-gray-400 text-xs md:text-sm tracking-[0.3em] uppercase mb-2" style={{
                fontFamily: 'Oswald, sans-serif'
              }} data-id="element-159">
                      Select Your Language / Elige Tu Idioma
                    </p>
                    <div className="w-8 h-px bg-[#c41e3a] mx-auto" data-id="element-160" />
                  </motion.div>

                  {/* Language buttons */}
                  <div className="relative z-10 flex w-full max-w-3xl px-4 gap-4 md:gap-8" data-id="element-161">
                    {/* English */}
                    <motion.button onClick={() => handleSelect('en')} onHoverStart={() => setHovering('en')} onHoverEnd={() => setHovering(null)} className="flex-1 flex flex-col items-center justify-center py-10 md:py-14 rounded-sm border border-white/10 relative overflow-hidden cursor-pointer" style={{
                background: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(8px)'
              }} initial={{
                opacity: 0,
                x: -30
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: 0.25
              }} whileTap={{
                scale: 0.97
              }} aria-label="Continue in English" data-id="element-162">
                      <motion.div className="absolute inset-0" animate={{
                  background: hovering === 'en' ? 'rgba(196,30,58,0.12)' : 'rgba(0,0,0,0)'
                }} transition={{
                  duration: 0.3
                }} data-id="element-163" />
                      <motion.div className="absolute inset-0 rounded-sm border" animate={{
                  borderColor: hovering === 'en' ? 'rgba(196,30,58,0.6)' : 'rgba(255,255,255,0.1)'
                }} transition={{
                  duration: 0.3
                }} data-id="element-164" />
                      <span className="text-4xl mb-4 relative z-10" aria-hidden="true" data-id="element-165">
                        🇺🇸
                      </span>
                      <span className="text-white text-2xl md:text-4xl font-bold tracking-[0.15em] uppercase relative z-10" style={{
                  fontFamily: 'Oswald, sans-serif'
                }} data-id="element-166">
                        English
                      </span>
                      <motion.span className="text-[#c41e3a] text-xs tracking-[0.3em] uppercase mt-2 relative z-10" style={{
                  fontFamily: 'Oswald, sans-serif'
                }} animate={{
                  opacity: hovering === 'en' ? 1 : 0.4
                }} transition={{
                  duration: 0.3
                }} data-id="element-167">
                        Continue →
                      </motion.span>
                    </motion.button>

                    {/* Spanish */}
                    <motion.button onClick={() => handleSelect('es')} onHoverStart={() => setHovering('es')} onHoverEnd={() => setHovering(null)} className="flex-1 flex flex-col items-center justify-center py-10 md:py-14 rounded-sm border border-white/10 relative overflow-hidden cursor-pointer" style={{
                background: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(8px)'
              }} initial={{
                opacity: 0,
                x: 30
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: 0.25
              }} whileTap={{
                scale: 0.97
              }} aria-label="Continuar en Español" data-id="element-168">
                      <motion.div className="absolute inset-0" animate={{
                  background: hovering === 'es' ? 'rgba(196,30,58,0.12)' : 'rgba(0,0,0,0)'
                }} transition={{
                  duration: 0.3
                }} data-id="element-169" />
                      <motion.div className="absolute inset-0 rounded-sm border" animate={{
                  borderColor: hovering === 'es' ? 'rgba(196,30,58,0.6)' : 'rgba(255,255,255,0.1)'
                }} transition={{
                  duration: 0.3
                }} data-id="element-170" />
                      <span className="text-4xl mb-4 relative z-10" aria-hidden="true" data-id="element-171">
                        🇨🇺
                      </span>
                      <span className="text-white text-2xl md:text-4xl font-bold tracking-[0.15em] uppercase relative z-10" style={{
                  fontFamily: 'Oswald, sans-serif'
                }} data-id="element-172">
                        Español
                      </span>
                      <motion.span className="text-[#c41e3a] text-xs tracking-[0.3em] uppercase mt-2 relative z-10" style={{
                  fontFamily: 'Oswald, sans-serif'
                }} animate={{
                  opacity: hovering === 'es' ? 1 : 0.4
                }} transition={{
                  duration: 0.3
                }} data-id="element-173">
                        Continuar →
                      </motion.span>
                    </motion.button>
                  </div>

                  {/* Bottom tagline */}
                  <motion.p className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-600 text-[10px] tracking-[0.3em] uppercase whitespace-nowrap z-10" style={{
              fontFamily: 'Oswald, sans-serif'
            }} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} data-id="element-174">
                    Hialeah Gardens, FL · Est. Since Day One
                  </motion.p>
                </motion.div>}
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>
    </>;
}